import React from 'react';
import PropTypes from 'prop-types';

import { TopBar, Status, Dropdown, highlightText, simplify } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Event from '../../../../models/eventModel';
import { usePermissions } from '../../../../user/permissions';
import LoadingCard from '../../../loadingCard';
import Style from '../topBarModules.module.css';
import { mappingColorStatus } from '../../../../pages/widgets/eventListModal';
import { normalizeEventSearchInput } from '../../../../utils/eventSearch';

function SwitchEventStatus({ event, words = [] }) {
    const { t } = useTranslation();
    const { publication_status } = event;
    return (
        <div className={Style['switch-event__status-container']}>
            <Status responsive color={mappingColorStatus(publication_status)} text={t(`eventStatus.${publication_status}`)} />
            {event.id && <div className={Style['switch-event__id']}>{highlightText(`E ${event.id}`, words)}</div>}
        </div>
    );
}

SwitchEventStatus.propTypes = {
    event: PropTypes.object.isRequired,
    words: PropTypes.arrayOf(PropTypes.string)
};

function SwitchEventItem({ event, organization, onChangeShow, words = [] }) {
    const { i18n } = useTranslation();
    const history = useHistory();
    const subtitle = event.start_date ? new Intl.DateTimeFormat(i18n.language).format(new Date(event.start_date)) : null;
    return (
        <Dropdown.Item
            key={event.id}
            subtitle={subtitle}
            status={<SwitchEventStatus event={event} words={words} />}
            onClick={() => {
                history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                onChangeShow(false);
            }}
        >
            {highlightText(event.name, words)}
        </Dropdown.Item>
    );
}

SwitchEventItem.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    onChangeShow: PropTypes.func.isRequired,
    words: PropTypes.arrayOf(PropTypes.string)
};

export default function SwitchEvent({ organization = null, minimal = false }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [show, setShow] = React.useState(null);
    const [filteredEvents, setFilteredEvents] = React.useState([]);
    const [search, setSearch] = React.useState(null);
    const permissions = usePermissions();

    const hasCapacity = React.useMemo(() => {
        if (!organization) {
            return false;
        }
        return permissions.hasCapacities(['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT'], organization.id);
    }, [organization]);

    const today = new Date();
    const defaultQueryParams = {
        publication_status__in: 'draft,published',
        end_date: `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`,
        ordering: 'start_date'
    };
    const [, loading, sync] = Event.useApiModel(
        {
            organizationId: organization?.id,
            query: defaultQueryParams,
            launch: Boolean(organization),
            onSuccess: events => {
                setFilteredEvents(events);
            }
        },
        [organization]
    );

    const onFooterClick = React.useCallback(() => {
        history.push(`/ticket/O${organization?.id}/events`);
        setShow(false);
    }, [history, organization]);

    const onChange = React.useCallback(
        (synth, search) => {
            const normalizedEventSearch = normalizeEventSearchInput(search);
            const _queryParams = normalizedEventSearch?.search ? { search: normalizedEventSearch.search } : defaultQueryParams;
            setSearch(search || null);
            sync({ query: _queryParams });
        },
        [sync, defaultQueryParams]
    );

    const onReset = React.useCallback(() => {
        setSearch(null);
        sync({ query: defaultQueryParams });
    }, [sync, defaultQueryParams]);

    //Set show with null, for automatic control open/close by dropdownContainer
    const onClick = React.useCallback(() => {
        setShow(null);
    }, []);

    // Compute the list of events visible based on the current search input
    const visibleEvents = React.useMemo(() => {
        const normalizedEventSearch = normalizeEventSearchInput(search);
        if (normalizedEventSearch?.idStartsWith) {
            return filteredEvents.filter(event => String(event.id).startsWith(normalizedEventSearch.idStartsWith));
        }
        return filteredEvents;
    }, [filteredEvents, search]);

    // Compute search words for highlighting matches in event title and ID
    const searchWords = React.useMemo(() => {
        const normalizedEventSearch = normalizeEventSearchInput(search);
        if (!normalizedEventSearch) {
            return [];
        }

        const value = normalizedEventSearch.idStartsWith ? normalizedEventSearch.search : search;
        return [
            ...new Set(
                value
                    .split(/\s+/)
                    .map(simplify)
                    .filter(Boolean)
            )
        ];
    }, [search]);

    if (!organization || !hasCapacity || minimal) {
        return null;
    }

    return (
        <TopBar.Switch
            closeOnListClick={false}
            value={t('ticket.switchEvent.search_placeholder')}
            onLeaving={onReset}
            searchProps={{
                value: search,
                onChange: onChange,
                handleReset: onReset
            }}
            text={t('ticket.switchEvent.cta')}
            headerText={search ? t('ticket.switchEvent.header_with_search') : t('ticket.switchEvent.header')}
            footerText={t('ticket.switchEvent.footer_cta')}
            onFooterClick={onFooterClick}
            initial={show}
            onClick={onClick}
        >
            <LoadingCard loading={loading} height={'200px'}>
                {visibleEvents.length ? (
                    visibleEvents.map(event => <SwitchEventItem key={event.id} event={event} organization={organization} onChangeShow={setShow} words={searchWords} />)
                ) : (
                    <div className={Style['switch-event__no-result']}>{t('ticket.switchEvent.no_results')}</div>
                )}
            </LoadingCard>
        </TopBar.Switch>
    );
}
